/**
 * Content Item Editor
 * Form for editing content items with field-type-specific inputs
 */

import React, { useState } from 'react';
import { Save, X, Globe, Archive } from 'lucide-react';
import type { ContentType, ContentItem, ContentField } from '../../types';

// ============================================================================
// TYPES
// ============================================================================

interface ContentItemEditorProps {
  contentType: ContentType;
  item: ContentItem;
  onSave: (data: Record<string, any>) => void;
  onCancel: () => void;
}

// ============================================================================
// FIELD RENDERERS
// ============================================================================

interface FieldInputProps {
  field: ContentField;
  value: any;
  onChange: (value: any) => void;
}

function FieldInput({ field, value, onChange }: FieldInputProps) {
  switch (field.type) {
    case 'text':
    case 'url':
    case 'email':
      return (
        <input
          type={field.type === 'url' ? 'url' : field.type === 'email' ? 'email' : 'text'}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Enter ${field.label.toLowerCase()}...`}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
      );

    case 'richtext':
    case 'json':
      return (
        <textarea
          value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value || ''}
          onChange={(e) => {
            if (field.type === 'json') {
              try {
                onChange(JSON.parse(e.target.value));
              } catch {
                onChange(e.target.value);
              }
            } else {
              onChange(e.target.value);
            }
          }}
          placeholder={field.type === 'json' ? '{}' : `Enter ${field.label.toLowerCase()}...`}
          className={`w-full border rounded-lg px-3 py-2 text-sm h-32 ${
            field.type === 'json' ? 'font-mono' : ''
          }`}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
      );

    case 'boolean':
      return (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded"
          />
          <span className="text-sm text-gray-700">{field.label}</span>
        </label>
      );

    case 'date':
      return (
        <input
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
      );

    case 'datetime':
      return (
        <input
          type="datetime-local"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
      );

    case 'color':
      return (
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={value || '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 w-9 rounded border cursor-pointer"
          />
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#000000"
            className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono"
          />
        </div>
      );

    case 'select':
      return (
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Select...</option>
          {(field.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case 'multi_select':
      return (
        <div className="space-y-1">
          {(field.options || []).map((opt) => (
            <label key={opt.value} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={(value || []).includes(opt.value)}
                onChange={(e) => {
                  const current = value || [];
                  if (e.target.checked) {
                    onChange([...current, opt.value]);
                  } else {
                    onChange(current.filter((v: string) => v !== opt.value));
                  }
                }}
                className="rounded"
              />
              <span className="text-sm text-gray-700">{opt.label}</span>
            </label>
          ))}
        </div>
      );

    case 'image':
    case 'file':
      return (
        <div className="space-y-2">
          <input
            type="url"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`${field.type === 'image' ? 'Image' : 'File'} URL...`}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
          {field.type === 'image' && value && (
            <img
              src={value}
              alt="Preview"
              className="h-24 w-auto rounded border object-cover"
            />
          )}
        </div>
      );

    case 'reference':
    case 'multi_reference':
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Referenced item ID..."
          className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
        />
      );

    default:
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
      );
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ContentItemEditor({
  contentType,
  item,
  onSave,
  onCancel,
}: ContentItemEditorProps) {
  const [data, setData] = useState<Record<string, any>>(item.data || {});
  const [status, setStatus] = useState(item.status || 'draft');

  const updateField = (fieldName: string, value: any) => {
    setData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSave = () => {
    onSave(data);
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Status Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
          >
            <Save className="h-4 w-4" />
            Save
          </button>
        </div>
      </div>

      {/* Fields */}
      <div className="space-y-5">
        {contentType.fields.map((field) => (
          <div key={field.id}>
            {field.type !== 'boolean' && (
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            <FieldInput
              field={field}
              value={data[field.name]}
              onChange={(value) => updateField(field.name, value)}
            />
          </div>
        ))}
      </div>

      {/* Slug */}
      <div className="mt-6 pt-6 border-t">
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          URL Slug
        </label>
        <input
          type="text"
          value={data._slug || ''}
          onChange={(e) => updateField('_slug', e.target.value)}
          placeholder="auto-generated-from-title"
          className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
        />
        <p className="text-xs text-gray-500 mt-1">
          Used in the URL when displaying this item on a page.
        </p>
      </div>
    </div>
  );
}

export default ContentItemEditor;
